import { StudioVisualizationType } from '@blockworks/platform/api/research/studio/visualizations';
import { Bit } from '@blockworks/ui/bit';
import { FlexBox } from '@blockworks/ui/flex-box';
import { Logo } from '@blockworks/ui/logo';
import { cn } from '@blockworks/ui/system';

import { useVisualizationContext } from '../../context';
import { Pie, Sankey, Timeseries } from '../charts';
import { Counter } from '../counter';
import { Table } from '../table';

import { VISUALIZATION_INNER_WRAPPER_HTML_ID, VISUALIZATION_WORDMARK_HTML_ID } from './visualization-renderer.model';

interface VisualizationInnerWrapperProps {
    children: React.ReactNode;
    type: StudioVisualizationType;
    hideWordmark?: boolean;
}

const VisualizationInnerWrapper = ({ children, type, hideWordmark = false }: VisualizationInnerWrapperProps) => {
    return (
        <FlexBox
            pos="relative"
            w="full"
            h="full"
            maxH="full"
            maxW="full"
            overflow={type === StudioVisualizationType.Timeseries ? 'hidden' : 'auto'}
            p={type === StudioVisualizationType.Table ? 3 : 0}
            id={VISUALIZATION_INNER_WRAPPER_HTML_ID}
        >
            <Bit.div
                pos="absolute"
                alignItems={'center'}
                justifyContent={'center'}
                display={'flex'}
                top={0}
                left={0}
                right={0}
                bottom={0}
                className="z-[0]"
                id={VISUALIZATION_WORDMARK_HTML_ID}
            >
                <Logo
                    className={cn('h-[50px] w-[300px] max-w-[50%] grayscale-100 opacity-[15%]', {
                        hidden: hideWordmark,
                    })}
                />
            </Bit.div>
            {children}
        </FlexBox>
    );
};

const VisualizationWrapper = ({ children }: { children: React.ReactNode }) => {
    const { visualizationRef } = useVisualizationContext();
    return (
        <FlexBox w="full" h="full" col pos="relative" ref={visualizationRef}>
            {children}
        </FlexBox>
    );
};

const getVisualizationComponent = (type: StudioVisualizationType) => {
    switch (type) {
        case StudioVisualizationType.Pie:
            return Pie;
        case StudioVisualizationType.Table:
            return Table;
        case StudioVisualizationType.Timeseries:
            return Timeseries;
        case StudioVisualizationType.Counter:
            return Counter;
        case StudioVisualizationType.Sankey:
            return Sankey;
        default:
            return null;
    }
};

export { getVisualizationComponent, VisualizationInnerWrapper, VisualizationWrapper };
